import moment from "moment";
import { saveAs } from "file-saver";
import { ValueStrings } from "./constantsString";
import { getItemSession } from "../utils/session";
import { getadvanceSettings } from "../network/cipAPI";
import {
  storeApp,
  storeSuggestedSettings,
} from "../Store/TemplateStore/template.action";
import { API_URLS } from "../network/appUrls";
import { getOptimizationTargetKey } from "../pages/predict/predictUtils";
import CAStore from "../Store/TemplateStore/template.store";
import {
  COMPARE_KEYS,
  UNIT_CONVERSION_CONFIG,
} from "../constants/global.constants";

let currentPlantId;
const { CIP_DATE_TIME_FORMAT, DATE_TIME_SECONDS_FORMAT } = ValueStrings;

export const updatePlantId = (plantId) => {
  if (plantId !== undefined) currentPlantId = plantId;
};

export const extractPlantId = () => {
  if (currentPlantId) {
    return currentPlantId;
  } else {
    var lastVisitedURL = getItemSession("lastVisitedURL");
    if (lastVisitedURL) {
      var searchParams = new URLSearchParams(lastVisitedURL.split("?")[1]);
      const plantId = searchParams.get("plantId");
      updatePlantId(plantId);
      return plantId;
    }
  }
};

export const getPlantNameFromState = () => {
  const plantName =
    CAStore?.getState?.()?.plantName || getItemSession("plantName");
  if (plantName) {
    return plantName.charAt(0).toUpperCase() + plantName.slice(1).trim();
  }
  return "";
};

export const getDuplicateItems = (array) => {
  const seen = new Set();
  const duplicates = [];

  for (const item of array) {
    const itemString = JSON.stringify(item);

    if (
      seen.has(itemString) &&
      !duplicates.some((dup) => JSON.stringify(dup) === itemString)
    ) {
      duplicates.push(item);
    } else {
      seen.add(itemString);
    }
  }
  duplicates.sort((a, b) => {
    if (!a && !b) {
      return 0;
    } else if (!a) {
      return 1;
    } else if (!b) {
      return -1;
    } else {
      return new Date(a) - new Date(b);
    }
  });
  return duplicates;
};

export const getControlId = (text = "dupont", cntrlType) => {
  const label = text
    .toLowerCase()
    .replace(/\s/g, "_", "_")
    .replace(/[^a-z0-9_]/g, "");
  return cntrlType ? `${label}_${cntrlType}` : label;
};

export const getChangedData = (updatedData, initialData) => {
  if (Array.isArray(updatedData) || updatedData === "") {
    return updatedData;
  }
  const dataDiff = {};
  for (const key in updatedData) {
    if (initialData) {
      if (initialData[key] !== null && typeof initialData[key] === "object") {
        dataDiff[key] = getChangedData(updatedData[key], initialData[key]);
        if (typeof dataDiff[key] === "object") {
          //need this remove && condition, BE to implement empty attribute
          if (Object.keys(dataDiff[key]).length === 0 && key !== "Attributes") {
            delete dataDiff[key];
          }
        }
      } else if (initialData[key] !== updatedData[key]) {
        dataDiff[key] = updatedData[key];
      }
    }
  }
  return dataDiff;
};

export const UpdateSetWithKey = (prevData, key, value) => {
  const updatedData = { ...prevData };
  updatedData[key] = value;
  return updatedData;
};

export const getFormattedDate = (dateString) => {
  const date = moment(dateString).isValid();
  return date ? moment(dateString).format(ValueStrings.dateFormat) : "-";
};

export const getSelectedPlantTrainInfo = (plantData, plantId) => {
  const plantInfo = plantData.find(
    (plant) => String(plant.plant_id) === String(plantId),
  );
  return plantInfo?.sel_train ?? 1;
};

export const updatePlantDataTrain = (plantData, plantId, trainNumber) =>
  plantData.map((plant) =>
    String(plant.plant_id) === String(plantId)
      ? { ...plant, sel_train: trainNumber }
      : plant,
  );

export const getIndex = (params, column) => {
  return column ? params.node.rowIndex + 1 : "";
};

// get date with start of the day if time not given
export const initializeDate = (dateString) => {
  if (!dateString) return null;
  let date = moment(dateString);
  return date.isValid() ? date.toDate() : null;
};

export const formatToLocalDateTime = (dateString) => {
  if (!dateString) return "-";

  try {
    const localMoment = moment.utc(dateString).local();

    if (!localMoment.isValid()) {
      return getFormattedDate(dateString);
    }

    return localMoment.format(DATE_TIME_SECONDS_FORMAT);
  } catch (error) {
    console.error("Error formatting date to local time:", error);
    return getFormattedDate(dateString);
  }
};

export const formatDate = (dateString, dateFormat = CIP_DATE_TIME_FORMAT) => {
  const date = moment(dateString);
  return date.isValid() ? date.format(dateFormat) : "-";
};

// Common function to convert moment date to Date object
export const convertMomentToDate = (dateValue) => {
  return moment(dateValue).toDate();
};

// navigate to add new plant page
export const navigateToCreateNewPlant = (navigate) => {
  const basePath = API_URLS.basePath;
  navigate(`${basePath}`, { state: { openAddPlant: true } });
};

/**
 * Unified unit converter - converts between base and target units
 * @param {number} value - The value to convert
 * @param {string} unit - The unit to convert to/from
 * @param {boolean} toBase - true = convert FROM unit TO base unit, false = convert FROM base TO unit (default)
 * @returns {number} Converted value
 *
 * Examples:
 *   unitConverter(1, "bar")  // base to bar: 1 * 1 = 1
 *   unitConverter(100, "kPa", true)  // kPa to base: 100 / 100 = 1
 *   unitConverter(32, "°F", true)  // °F to °C: (32 - 32) / (9/5) = 0
 */
export const unitConverter = (value, unit, toBase = false) => {
  if (value === null || value === undefined || !unit) {
    return value;
  }

  const { FLOW, PRESSURE, TEMPERATURE, CONDUCTIVITY } = UNIT_CONVERSION_CONFIG;

  let unitConfig;
  if (FLOW[unit]) unitConfig = FLOW;
  else if (PRESSURE[unit]) unitConfig = PRESSURE;
  else if (TEMPERATURE[unit]) unitConfig = TEMPERATURE;
  else if (CONDUCTIVITY[unit]) unitConfig = CONDUCTIVITY;
  else return value;

  const factor = unitConfig[unit];
  if (!factor) return value;

  let convertedValue;
  const isTemperature = unitConfig === TEMPERATURE;

  if (toBase) {
    // Convert FROM unit TO base
    if (isTemperature) {
      const { multiplier, offset } = factor;
      convertedValue = (parseFloat(value) - offset) / multiplier;
    } else {
      convertedValue = parseFloat(value) / factor;
    }
  } else {
    // Convert FROM base TO unit
    if (isTemperature) {
      const { multiplier, offset } = factor;
      convertedValue = parseFloat(value) * multiplier + offset;
    } else {
      convertedValue = parseFloat(value) * factor;
    }
  }

  return parseFloat(convertedValue.toFixed(2));
};

export const compareRelativeFields = (
  field,
  newSelectedReference,
  compareList,
  t,
) => {
  let comapreResult = null;

  compareList.forEach(([mainKey, compareKey]) => {
    if (field === mainKey || field === compareKey) {
      const mainVal = parseFloat(newSelectedReference[mainKey]);
      const compareVal = parseFloat(newSelectedReference[compareKey]);
      if (mainVal <= compareVal) {
        if (field === mainKey) {
          comapreResult = [COMPARE_KEYS.GREATER, compareKey];
        } else if (field === compareKey) {
          comapreResult = [COMPARE_KEYS.LESS, mainKey];
        }
      }
    }
  });
  return comapreResult;
};

export const generateFileName = (fileLabel) => {
  const plantName = getPlantNameFromState();
  const fileName = `${plantName}_${moment().format(
    ValueStrings.dateFormatMonthDash,
  )}_${fileLabel}`;
  return fileName;
};

export const saveExcelFile = async (fileName, workbook) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const excelData = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(excelData, fileName);
};

export const getRequestStatusText = (t, status) => {
  switch (status) {
    case 1:
      return t("adminPage.request_submitted");
    case 2:
      return t("adminPage.accepted");
    case 3:
      return t("adminPage.rejected");
    case 4:
      return t("adminPage.revoked");
    default:
      return "-";
  }
};
export const getUploadedDaysAgo = (t, uploadedAt) => {
  if (!uploadedAt) return "-";

  try {
    const uploadDate = new Date(uploadedAt);
    const now = new Date();

    const uploadDateOnly = new Date(
      uploadDate.getFullYear(),
      uploadDate.getMonth(),
      uploadDate.getDate(),
    );
    const nowDateOnly = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );

    const diffInMs = nowDateOnly - uploadDateOnly;
    const daysAgo = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    let uploadedDaysAgoText;
    if (daysAgo === 0) {
      uploadedDaysAgoText = t("ValueStrings.today");
    } else if (daysAgo === 1) {
      uploadedDaysAgoText = t("ValueStrings.yesterday");
    } else {
      uploadedDaysAgoText = `${daysAgo} ${
        daysAgo > 1 ? t("ValueStrings.days") : t("ValueStrings.day")
      } ${t("ValueStrings.ago")}`;
    }
    return uploadedDaysAgoText;
  } catch (error) {
    return "-";
  }
};

export const isNullOrUndefinedOrEmpty = (value) =>
  value === null || value === undefined || value === "";

export const emptyValueFormatter = (params) => {
  const { value } = params;
  if (isNullOrUndefinedOrEmpty(value)) {
    return "-";
  }
  return value;
};

export const getDatasetDetails = ({
  owner_username,
  plantName,
  water_type,
  min_date,
  max_date,
}) => {
  const minDate = getFormattedDate(min_date);
  const maxDate = getFormattedDate(max_date);
  return ` ${owner_username}_ ${plantName}_ ${water_type}
    ${minDate && minDate !== "-" ? `_ ${minDate}` : ""}
    ${maxDate && maxDate !== "-" ? `to ${maxDate}` : ""}`;
};

/**
 * Convert API settings to Redux store format
 * @param {Object} settings - API settings object
 * @returns {Object} Converted settings for Redux store
 */
export const mapSettingsToRedux = (settings) => ({
  clusterPoints: settings.n_neighbors,
  medianCurvePoints: settings.window,
  maxRelativeDistance: settings.n,
  permeateFlowDropThreshold: settings.pf_threshold,
  pressureDropThreshold: settings.pressure_drop_threshold,
  pressureDropThresholdAbsolute: settings.pressure_drop_abs_threshold,
  saltPassageIncreaseThreshold: settings.salt_passage_threshold,
  minDaysBetweenCIPs: settings.min_day_between_cips,
  maxDaysBetweenCIPs: settings.max_days_between_cips,
  cipIdentificationResolution: settings.k,
  minIdleDaysShutdown: settings.shutdown_length,
  useSaltPassageChange: settings.use_salt_passage,
  minStabilizationDaysAfterCIP: settings.min_time,
  maxStabilizationDaysAfterCIP: settings.max_time,
  minPointsForCleaningEffectiveness: settings.min_num_points,
  deafultStabilizationDaysAfterCIP: settings.stabilization_days,
  foulingAggressivenessFactor: settings.top_exp,
  numberOfIterationsToFindOptima: settings.max_evals,
  adaptPermeateFlowDropThreshold: settings.adapt_pf_trend,
  adaptPressureDropThreshold: settings.adapt_pressure_drop_trend,
  adaptSaltPassageThreshold: settings.adapt_salt_passage_trend,
  simulationImproveAccuracy: settings.simulation_improve_accuracy,
  failureProbabilityThreshold: settings.failure_probability_threshold,
  optimizationObjective: settings.optimization_target,
});

/**
 * Fetches advance settings and formats suggested values for export
 * @param {string} selectedTrain - The selected train number
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} originalAppState - The original app state to restore
 * @returns {Promise<Object|null>} Mapped suggested settings or null
 */
export const fetchSuggestedSettings = async (
  selectedTrain,
  dispatch,
  originalAppState,
) => {
  try {
    dispatch(storeApp(null));
    const advancedSettings = await getadvanceSettings(selectedTrain);

    if (advancedSettings?.status === 200 && advancedSettings?.data) {
      const { suggested_settings: suggestedData = {} } = advancedSettings.data;

      if (suggestedData && Object.keys(suggestedData).length > 0) {
        const mappedSuggestedSettings = mapSettingsToRedux(suggestedData);
        dispatch(storeSuggestedSettings(mappedSuggestedSettings));
        return mappedSuggestedSettings;
      }
    }

    return null;
  } catch (err) {
    console.error("Failed to fetch advance settings:", err);
    return null;
  } finally {
    dispatch(storeApp(originalAppState));
  }
};

/**
 * Format suggested settings for Excel export
 * @param {Object} mappedSuggestedSettings - Mapped settings object
 * @param {Function} t - Translation function
 * @returns {Array|null} Formatted settings array or null
 */
export const formatSuggestedSettings = (mappedSuggestedSettings, t) => {
  if (
    !mappedSuggestedSettings ||
    Object.keys(mappedSuggestedSettings).length === 0
  ) {
    return null;
  }

  return [
    {
      clusterPoints: mappedSuggestedSettings.clusterPoints ?? null,
      medianCurvePoints: mappedSuggestedSettings.medianCurvePoints ?? null,
      maxRelativeDistance: mappedSuggestedSettings.maxRelativeDistance ?? null,
      permeateFlowDropThreshold:
        mappedSuggestedSettings.permeateFlowDropThreshold ?? null,
      pressureDropThreshold:
        mappedSuggestedSettings.pressureDropThreshold ?? null,
      pressureDropThresholdAbsolute:
        mappedSuggestedSettings.pressureDropThresholdAbsolute ?? null,
      useSaltPassageChange:
        mappedSuggestedSettings.useSaltPassageChange != null
          ? mappedSuggestedSettings.useSaltPassageChange
            ? t("ValueStrings.yes")
            : t("ValueStrings.no")
          : null,
      saltPassageIncreaseThreshold:
        mappedSuggestedSettings.saltPassageIncreaseThreshold ?? null,
      minDaysBetweenCIPs: mappedSuggestedSettings.minDaysBetweenCIPs ?? null,
      maxDaysBetweenCIPs: mappedSuggestedSettings.maxDaysBetweenCIPs ?? null,
      cipIdentificationResolution:
        mappedSuggestedSettings.cipIdentificationResolution ?? null,
      minIdleDaysShutdown: mappedSuggestedSettings.minIdleDaysShutdown ?? null,
      maxStabilizationDaysAfterCIP:
        mappedSuggestedSettings.maxStabilizationDaysAfterCIP ?? null,
      minStabilizationDaysAfterCIP:
        mappedSuggestedSettings.minStabilizationDaysAfterCIP ?? null,
      minPointsForCleaningEffectiveness:
        mappedSuggestedSettings.minPointsForCleaningEffectiveness ?? null,
      deafultStabilizationDaysAfterCIP:
        mappedSuggestedSettings.deafultStabilizationDaysAfterCIP ?? null,
      foulingAggressivenessFactor:
        mappedSuggestedSettings.foulingAggressivenessFactor ?? null,
      numberOfIterationsToFindOptima:
        mappedSuggestedSettings.numberOfIterationsToFindOptima ?? null,
      adaptPermeateFlowDropThreshold:
        mappedSuggestedSettings.adaptPermeateFlowDropThreshold != null
          ? mappedSuggestedSettings.adaptPermeateFlowDropThreshold
            ? t("ValueStrings.yes")
            : t("ValueStrings.no")
          : null,
      adaptPressureDropThreshold:
        mappedSuggestedSettings.adaptPressureDropThreshold != null
          ? mappedSuggestedSettings.adaptPressureDropThreshold
            ? t("ValueStrings.yes")
            : t("ValueStrings.no")
          : null,
      adaptSaltPassageThreshold:
        mappedSuggestedSettings.adaptSaltPassageThreshold != null
          ? mappedSuggestedSettings.adaptSaltPassageThreshold
            ? t("ValueStrings.yes")
            : t("ValueStrings.no")
          : null,
      simulationImproveAccuracy:
        mappedSuggestedSettings.simulationImproveAccuracy != null
          ? mappedSuggestedSettings.simulationImproveAccuracy
            ? t("ValueStrings.yes")
            : t("ValueStrings.no")
          : null,
      failureProbabilityThreshold:
        mappedSuggestedSettings.failureProbabilityThreshold ?? null,
      optimizationTarget:
        mappedSuggestedSettings.optimizationObjective != null
          ? t(
              `advanceSettings.${getOptimizationTargetKey(mappedSuggestedSettings.optimizationObjective)}`,
            )
          : null,
    },
  ];
};
